import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { useFoundingSpotsRemaining } from "@/hooks/useFoundingSpotsRemaining";
import { useIsPro } from "@/hooks/useIsPro";
import { Check } from "lucide-react";

const FOUNDING_TOTAL = 5;

const freeFeatures = [
  { label: "Cash Runway Number", desc: "How many days your shop can survive without new sales." },
  { label: "Stuck Stock Name", desc: "Your #1 slow-moving product, identified." },
  { label: "Weekly Business Tip", desc: "One plain English insight, every week." },
  { label: "Digital Sales Log", desc: "Track every sale on your phone. No notebooks." },
  { label: "30-Day Snapshot", desc: "Your most recent month, always organised." },
];

const soloFeatures = [
  { label: "Transport & Logistics", desc: "Purchase orders, deliveries, and stock receipt. Keep stock flowing." },
  { label: "30-Day Cash Forecast", desc: "See if you can afford rent next month — before next month arrives." },
  { label: "Dead Stock Recovery Plan", desc: "Exactly how much Kwacha is stuck. Exactly how to get it back." },
  { label: "Smart Reorder Alerts", desc: "Never run out of best-sellers. Know what to buy and when." },
  { label: "Money-Maker Identification", desc: "Your most and least profitable products, ranked automatically." },
  { label: "Automated P&L Reports", desc: "Professional statements ready for your bank or loan application." },
  { label: "WhatsApp Report Sharing", desc: "Send your daily performance to anyone in one tap." },
  { label: "Calculation Transparency", desc: "Every number has a 'How was this calculated?' explanation." },
];

const growthFeatures = [
  { label: "Branch Switcher in header", desc: "Switch between locations from the top bar. Dashboard, BI Analyst, sales, expenses, and inventory all filter by your selection." },
  { label: "Branch vs. Branch Comparison", desc: "Table showing revenue, expenses, items, and dead stock per branch. See which location performs best." },
  { label: "Multi-Branch Command Center", desc: "All your locations. One screen. Nothing hidden." },
  { label: "Transport & Logistics by branch", desc: "POs, deliveries, receive per location. Logistics alerts across all branches." },
  { label: "Inter-Branch Stock Transfers", desc: "Move slow stock from Shop A to Shop B before reordering." },
  { label: "Consolidated Executive Summary", desc: "One report for your entire business. Built for investors." },
  { label: "Expansion Readiness Score", desc: "Know when you have the cash flow to safely open a new branch." },
  { label: "Manager Performance Alerts", desc: "Suspicious stock or cash levels at any branch — you'll know first." },
];

const discoveries = [
  { stat: "K3,000+", text: "in dead stock they didn't know existed on their shelves — every month." },
  { stat: "2 weeks", text: "of warning before a cash crisis, instead of finding out when the account was empty." },
  { stat: "1 product", text: "consistently under-ordered. Now their single fastest-growing revenue line." },
  { stat: "1 branch", text: "quietly draining the others. Caught before it caused real damage." },
];

const faqs = [
  { q: "Is the free plan really free forever?", a: "Yes. No credit card required. No expiry date. Start free and upgrade only when you're ready." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel from your settings at any time. Your data stays safe for 12 months after cancellation." },
  { q: "What if my payment fails?", a: "We give you a 3-day grace period — full Pro access continues while you sort it out. No sudden lockouts." },
  { q: "Is my shop data safe?", a: "Your data is encrypted and belongs to you. We never sell it, share it, or use it for anything other than your insights." },
  { q: "What if I'm not satisfied?", a: "If ReviewBoost Pro doesn't show you something valuable in your first 30 days, contact us. We'll work with you — or refund you." },
  { q: "Do you support multiple locations?", a: "Pro Solo covers 1 branch. Pro Growth supports up to 3 branches with full consolidated reporting." },
];

function FeatureItem({ label, desc, green }: { label: string; desc: string; green: boolean }) {
  return (
    <div className="flex gap-2.5 py-2 border-t border-border">
      <Check className={`h-4 w-4 shrink-0 mt-0.5 ${green ? "text-primary" : "text-muted-foreground"}`} />
      <p className="text-sm text-muted-foreground leading-snug">
        <strong className="text-foreground font-semibold">{label}.</strong> {desc}
      </p>
    </div>
  );
}

export default function PricingPage() {
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const { openPaywall } = usePaywallModal();
  const isPro = useIsPro();
  const foundingSpots = useFoundingSpotsRemaining();
  const foundingRemaining = typeof foundingSpots === "number" ? foundingSpots : 0;

  const soloPrice = billing === "annual" ? "8,500" : "900";
  const soloSuffix = billing === "annual" ? "/year" : "/mo";
  const growthPrice = billing === "annual" ? "17,000" : "1,800";
  const growthSuffix = billing === "annual" ? "/year" : "/mo";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-[1060px] mx-auto px-6 py-16 sm:py-[72px] pb-28 sm:pb-[120px]">

        {/* HEADER */}
        <div className="mb-12 sm:mb-14 animate-in fade-in duration-500">
          <p className="text-xs font-semibold text-muted-foreground tracking-wider uppercase mb-3">
            Pricing
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-foreground mb-3 max-w-[560px]">
            Find a plan for<br />your shop.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-[520px]">
            ReviewBoost Pro is the operating system for your shop. We transform raw sales data into financial clarity, identifying every Kwacha lost to inefficiency and every opportunity for expansion.
          </p>
        </div>

        {/* COST ANCHOR */}
        <div className="mb-8 animate-in fade-in duration-500 delay-75 fill-mode-both">
          <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-destructive/10 border border-destructive/30">
            <div className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
            <p className="text-sm text-destructive/90 leading-snug">
              The average Zambian retailer loses <strong>K3,500 every month</strong> to problems they cannot see.{" "}
              <span className="text-foreground font-semibold">Pro Solo costs K900.</span>
            </p>
          </div>
        </div>

        {/* BILLING TOGGLE */}
        <div className="mb-7 animate-in fade-in duration-500 delay-100 fill-mode-both">
          <div className="inline-flex bg-muted rounded-lg p-0.5 gap-0.5">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${billing === "monthly" ? "bg-background text-foreground font-semibold shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling("annual")}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${billing === "annual" ? "bg-background text-foreground font-semibold shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              Annual
              <span className="bg-primary/20 text-primary text-[10px] font-bold px-2 py-0.5 rounded-full">
                SAVE MORE
              </span>
            </button>
          </div>
        </div>

        {/* PRICING CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16 sm:mb-20 items-start animate-in fade-in duration-500 delay-150 fill-mode-both">
          {/* FREE */}
          <div className="rounded-xl border border-border bg-card p-6 sm:p-7 flex flex-col hover:shadow-md hover:border-border/80 transition-all">
            <p className="text-sm font-semibold text-muted-foreground mb-3">Free Forever</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold tracking-tight">K0</span>
            </div>
            <p className="text-xs text-muted-foreground mb-1">5 products · 1 location · 30 days</p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              See your business for the first time. No card. No expiry.
            </p>
            <Button variant="outline" className="w-full mb-6" asChild>
              <Link to="/">Start Free — No Card</Link>
            </Button>
            <div className="border-t border-border pt-4">
              <p className="text-[11px] font-semibold text-muted-foreground tracking-wider uppercase mb-1">Included</p>
              {freeFeatures.map((f, i) => <FeatureItem key={i} {...f} green={false} />)}
            </div>
          </div>

          {/* PRO SOLO */}
          <div className="rounded-xl border-2 border-primary bg-card shadow-lg p-6 sm:p-7 flex flex-col relative hover:shadow-xl transition-all">
            <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[11px] font-semibold px-4 py-1 rounded-b-lg tracking-wider uppercase whitespace-nowrap">
              Most Popular
            </div>
            <p className="text-sm font-semibold text-foreground mb-3 mt-2">Pro Solo</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold tracking-tight">K{soloPrice}</span>
              <span className="text-sm text-muted-foreground font-medium">{soloSuffix}</span>
            </div>
            {billing === "annual" ? (
              <p className="text-sm text-foreground font-medium mb-1">Save K2,300</p>
            ) : (
              <p className="text-sm text-muted-foreground mb-1">or K8,500/year · Save K2,300</p>
            )}
            <p className="text-xs text-muted-foreground mb-1">1 branch · Unlimited products</p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Stop losing money to problems you can't see. Know exactly what to do — every day.
            </p>
            {!isPro && (
              <Button className="w-full mb-6" onClick={() => openPaywall("upgrade_cta")}>
                Unlock Pro Solo
              </Button>
            )}
            <div className="border-t border-border pt-4">
              <p className="text-[11px] font-semibold text-muted-foreground tracking-wider uppercase mb-1">Everything in Free, plus</p>
              {soloFeatures.map((f, i) => <FeatureItem key={i} {...f} green={true} />)}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4">Cancel anytime</p>
          </div>

          {/* PRO GROWTH */}
          <div className="rounded-xl border border-border bg-card p-6 sm:p-7 flex flex-col hover:shadow-md hover:border-border/80 transition-all">
            <p className="text-sm font-semibold text-muted-foreground mb-3">Pro Growth</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold tracking-tight">K{growthPrice}</span>
              <span className="text-sm text-muted-foreground font-medium">{growthSuffix}</span>
            </div>
            {billing === "annual" ? (
              <p className="text-sm text-foreground font-medium mb-1">Save K4,600</p>
            ) : (
              <p className="text-sm text-muted-foreground mb-1">or K17,000/year · Save K4,600</p>
            )}
            <p className="text-xs text-muted-foreground mb-1">3 branches · Multi-location</p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              One underperforming branch can quietly drain your best one. See everything from one screen.
            </p>
            {!isPro && (
              <Button variant="outline" className="w-full mb-6" onClick={() => openPaywall("upgrade_cta")}>
                Unlock Pro Growth
              </Button>
            )}
            <div className="border-t border-border pt-4">
              <p className="text-[11px] font-semibold text-muted-foreground tracking-wider uppercase mb-1">Everything in Pro Solo, plus</p>
              {growthFeatures.map((f, i) => <FeatureItem key={i} {...f} green={false} />)}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4">Everything in Pro Solo · up to 3 branches</p>
          </div>
        </div>

        {/* FOUNDING MEMBER */}
        {foundingRemaining > 0 && (
          <div className="rounded-xl border border-border bg-muted/50 p-8 sm:p-10 mb-16 sm:mb-20 animate-in fade-in duration-500 delay-200 fill-mode-both">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-10 items-center">
              <div>
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-500/30 text-xs font-semibold px-2.5 py-1 rounded-full tracking-wide">
                    ⚡ Founding Member Offer
                  </span>
                  <span className="text-xs text-muted-foreground">{foundingRemaining} of {FOUNDING_TOTAL} spots left</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground mb-2">
                  You're early. That means something.
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-[500px] mb-5">
                  The first {FOUNDING_TOTAL} retailers to join get a price that <strong className="text-foreground">never changes</strong> — locked for life, no matter what happens to standard pricing.
                </p>
                <div className="flex gap-7 flex-wrap">
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground tracking-wider uppercase mb-1">Pro Solo</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold tracking-tight">K750</span>
                      <span className="text-sm text-muted-foreground">/mo · locked forever</span>
                    </div>
                    <p className="text-xs text-primary font-medium mt-0.5">Save K150/mo vs standard</p>
                  </div>
                  <div className="w-px bg-border" />
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground tracking-wider uppercase mb-1">Pro Growth</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold tracking-tight">K1,400</span>
                      <span className="text-sm text-muted-foreground">/mo · locked forever</span>
                    </div>
                    <p className="text-xs text-primary font-medium mt-0.5">Save K400/mo vs standard</p>
                  </div>
                </div>
              </div>
              <div className="text-center min-w-[160px]">
                <p className="text-[11px] font-semibold text-muted-foreground tracking-wider uppercase mb-2">Spots Left</p>
                <div className="flex gap-1 justify-center mb-2">
                  {Array.from({ length: FOUNDING_TOTAL }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-6 rounded-full ${i < foundingRemaining ? "bg-primary" : "bg-border"}`}
                    />
                  ))}
                </div>
                <p className="text-2xl font-bold tracking-tight mb-4">
                  {foundingRemaining}/{FOUNDING_TOTAL}
                </p>
                {!isPro && (
                  <Button onClick={() => openPaywall("upgrade_cta")} className="w-auto px-6 whitespace-nowrap">
                    Claim My Spot →
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SOCIAL PROOF */}
        <div className="mb-16 sm:mb-20 animate-in fade-in duration-500 delay-300 fill-mode-both">
          <p className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-2">Real outcomes</p>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-foreground mb-1">
            Zambian retailers using Pro have discovered:
          </h2>
          <p className="text-sm text-muted-foreground mb-7">
            They didn't hire a consultant. They paid <strong className="text-foreground">K900 a month.</strong>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {discoveries.map((d, i) => (
              <div
                key={i}
                className="rounded-xl border border-border bg-card p-6 hover:shadow-md hover:border-border/80 transition-all hover:-translate-y-0.5"
              >
                <p className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground leading-none mb-2">{d.stat}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{d.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16 sm:mb-20 animate-in fade-in duration-500 delay-300 fill-mode-both">
          <div className="h-px bg-border mb-12" />
          <h2 className="text-xl font-bold tracking-tight mb-7">
            Frequently asked questions.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 sm:gap-x-12">
            {faqs.map((item, i) => (
              <div key={i}>
                <p className="text-sm font-semibold text-foreground mb-1.5">{item.q}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* BOTTOM CTA */}
        <div className="border-t border-border pt-14 grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-8 sm:gap-10 animate-in fade-in duration-500 fill-mode-both">
          <div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight leading-tight mb-2">
              Your shop is talking.<br />
              <span className="text-muted-foreground">Start listening.</span>
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-[400px] mb-3">
              Every sale. Every slow week. Every supplier invoice. ReviewBoost Pro reads it all — and tells you exactly what it means.
            </p>
            <p className="text-sm text-muted-foreground">
              Questions?{" "}
              <a href="mailto:support@reviewboost24.com" className="text-foreground font-medium hover:underline">
                support@reviewboost24.com
              </a>
              {" "}· We respond within 1 hour.
            </p>
          </div>
          <div className="flex flex-col gap-2 min-w-[170px]">
            {!isPro && (
              <>
                <Button onClick={() => openPaywall("upgrade_cta")} className="w-full sm:w-auto px-6">
                  Unlock Pro Solo →
                </Button>
                <Button variant="outline" asChild className="w-full sm:w-auto px-6">
                  <Link to="/">Start Free</Link>
                </Button>
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
